import { AfterViewInit, Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Employee } from '@model/employee';
import { Shift, ShiftType } from '@model/reservation';
import { Settings } from '@model/tables-plan';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TablesPlanActions } from '@store/actions/tables-plan.actions';
import * as EmployeeSelectors from '@store/selectors/employee.selectors';
import * as TablesPlanSelectors from '@store/selectors/tables-plan.selectors';
import { Toast } from 'bootstrap';
import { Observable, Subject, takeUntil } from 'rxjs';
import { Common } from 'src/app/lib/common-methods';
import { ReservationService } from 'src/app/lib/reservation.service';
declare const bootstrap: any;

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss'
})
export class SettingsComponent implements OnInit, AfterViewInit, OnDestroy {

  destroy$: Subject<boolean> = new Subject<boolean>();

  @Output() hide = new EventEmitter<boolean>();

  @ViewChild('toast') toastElement!: ElementRef;

  toast: Toast | null = null;

  settings: Settings | null = null;
  shifts: Shift[] = [];
  hours: number[];
  mins: number[];

  oneToTwoCoversHours: number = 0;
  oneToTwoCoversMinutes: number = 0;
  threeToFourCoversHours: number = 0;
  threeToFourCoversMinutes: number = 0;
  fiveOrMoreCoversHours: number = 0;
  fiveOrMoreCoversMinutes: number = 0;

  settingsAreNew: boolean = false;

  message: string = '';

  counttryCodes = Common.countryCodeList.map(c => {
    return { display: `${c.code} (${c.country})`, value: c.code }
  });

  constructor(
    private router: Router,
    private translateService: TranslateService,
    private reservationService: ReservationService,
    private store: Store<{}>,
  ) {
    this.hours = Array(24).fill(0).map((x, i) => i);
    this.mins = Array(60).fill(0).map((x, i) => i);
  }

  tablesPlansettings$: Observable<{
    settings: Settings | null,
    shifts: Shift[],
  }> = this.store.pipe(select(TablesPlanSelectors.selectSettings));
  tablesPlansettings: {
    settings: Settings | null,
    shifts: Shift[],
  } = {
      settings: null,
      shifts: [],
    };

  loggedInemployee$: Observable<Employee> = this.store.pipe(select(EmployeeSelectors.selectLoggedInemployee));
  loggedInemployee: Employee | null = null;

  ngOnInit() {

    this.tablesPlansettings$.pipe(takeUntil(this.destroy$)).subscribe((tablesPlansettings) => {
      this.tablesPlansettings = tablesPlansettings;

      this.settings = JSON.parse(JSON.stringify(tablesPlansettings.settings));
      this.shifts = JSON.parse(JSON.stringify(tablesPlansettings.shifts));

      this.initEmpty();

      this.updateHoursAndMinutesFields();

    });

    this.loggedInemployee$.pipe(takeUntil(this.destroy$)).subscribe((loggedInemployee) => {
      this.loggedInemployee = loggedInemployee;
    });
  }

  ngAfterViewInit() {
    this.toast = bootstrap.Toast.getOrCreateInstance(this.toastElement.nativeElement);
  }

  updateHoursAndMinutesFields() {
    let time = Common.minutesToHoursAndMinutesFormat(this.settings?.oneToTwoCoversDuration);
    this.oneToTwoCoversHours = time.hours;
    this.oneToTwoCoversMinutes = time.minutes;
    time = Common.minutesToHoursAndMinutesFormat(this.settings?.threeToFourCoversDuration);
    this.threeToFourCoversHours = time.hours;
    this.threeToFourCoversMinutes = time.minutes;
    time = Common.minutesToHoursAndMinutesFormat(this.settings?.fiveOrMoreCoversDuration);
    this.fiveOrMoreCoversHours = time.hours;
    this.fiveOrMoreCoversMinutes = time.minutes;
  }

  updateSettingsMinutesFields() {
    if (this.settings == null) {
      this.initEmpty();
    }
    let minutes = Common.hoursAndMinutesToMinutesFormat(this.oneToTwoCoversHours, this.oneToTwoCoversMinutes);
    (this.settings as Settings).oneToTwoCoversDuration = minutes;
    minutes = Common.hoursAndMinutesToMinutesFormat(this.threeToFourCoversHours, this.threeToFourCoversMinutes);
    (this.settings as Settings).threeToFourCoversDuration = minutes;
    minutes = Common.hoursAndMinutesToMinutesFormat(this.fiveOrMoreCoversHours, this.fiveOrMoreCoversMinutes);
    (this.settings as Settings).fiveOrMoreCoversDuration = minutes;
  }

  initEmpty() {
    const date = new Date().getTime();
    let settingsId = Common.uniqueId();

    if (this.settings == null || this.settings.settingsId == null) {
      this.settings = {
        oneToTwoCoversDuration: 125,
        arrivalTimeAlarmNotification: 0,
        attendanceRate: 0,
        fiveOrMoreCoversDuration: 250,
        threeToFourCoversDuration: 175,
        onlineReservatioSettings: null,
        settingsId: settingsId,
        "smsDefaultCountryCode ": "+30",
        smsDefaultText: "",
        smsSender: "IHOST",
        timeperformed: date
      };

      this.settingsAreNew = true;
    }
    else {
      settingsId = this.settings.settingsId;
      this.settingsAreNew = false;
    }

    if (this.shifts == null || this.shifts.length == 0) {

      this.shifts = [
        {
          fromHour: 0,
          fromMin: 0,
          isActive: true,
          settingsId: settingsId,
          shiftId: Common.uniqueId(),
          timeperformed: date,
          title: "Midnight",
          toHour: 4,
          toMin: 0,
          type: 3
        },
        {
          fromHour: 13,
          fromMin: 0,
          isActive: true,
          settingsId: settingsId,
          shiftId: Common.uniqueId(),
          timeperformed: date,
          title: "Afternoon",
          toHour: 18,
          toMin: 59,
          type: 1
        },
        {
          fromHour: 8,
          fromMin: 0,
          isActive: true,
          settingsId: settingsId,
          shiftId: Common.uniqueId(),
          timeperformed: date,
          title: "Morning",
          toHour: 12,
          toMin: 59,
          type: 0
        },
        {
          fromHour: 19,
          fromMin: 0,
          isActive: true,
          settingsId: settingsId,
          shiftId: Common.uniqueId(),
          timeperformed: date,
          title: "Night",
          toHour: 23,
          toMin: 59,
          type: 2
        }
      ];

      console.log("Initialized Shifts!")
    }
  }

  cancel() {
    this.hide.emit();
  }

  save() {
    this.updateSettingsMinutesFields();

    let possibleError = this.getSettingsValidationMessage();

    if (possibleError != null) {
      this.message = possibleError;
      this.toast?.show();
      return;
    }

    this.store.dispatch({
      type: TablesPlanActions.saveSettings.type, restaurantSettings: {
        isConflict: false,
        modifiedBy: this.loggedInemployee?.surname,
        orderNumber: 1,
        pendingSyncOrder: 1,
        settings: this.settings,
        shifts: this.shifts,
        timePerformed: new Date().getTime() + "",
        typeOfModification: this.settingsAreNew === true ? "CREATED" : "EDITED"
      }
    });

    this.hide.emit();

    // todo: implement
    // this.api.saveSettings({
    //   isConflict: false,
    //   modifiedBy: localStorage.getItem("ihost-user"),
    //   orderNumber: 1,
    //   pendingSyncOrder: 1,
    //   settings: this.settings,
    //   shifts: this.shifts,
    //   timePerformed: new Date().getTime() + "",
    //   typeOfModification: this.settingsAreNew === true ? "CREATED" : "EDITED"
    // }).subscribe(data => {
    //   console.log(data);

    //   if (data.Status === true) {
    //     alert(TransPipe.getTrans("Settings Saved.|Οι ρυθμίσεις αποθηκεύτηκαν."));
    //     this.load();
    //   }
    //   else {
    //     alert(TransPipe.getTrans("Error saving settings!|Σφάλμα κατά την αποθήκευση των ρυθμίσεων"));
    //     this.load();
    //   }
    // }, (error) => {
    //   alert(TransPipe.getTrans("Error saving settings!|Σφάλμα κατά την αποθήκευση των ρυθμίσεων"));
    // });
  }

  getTitle(shiftType: ShiftType | undefined) {
    return this.reservationService.translateShiftTitle(shiftType);
  }

  getSettingsValidationMessage(): string | null {
  if (this.settings == null) return null;

  if (this.settings.oneToTwoCoversDuration == null || this.settings.oneToTwoCoversDuration < 1) {
    return this.translateService.instant('tablesPlan.settings.oneToTwoCoversDurationErrorMessage');
  }

  if (this.settings.threeToFourCoversDuration == null || this.settings.threeToFourCoversDuration < 1) {
    return this.translateService.instant('tablesPlan.settings.threeOrFourCoversDurationErrorMessage');
  }

  if (this.settings.fiveOrMoreCoversDuration == null || this.settings.fiveOrMoreCoversDuration < 1) {
    return this.translateService.instant('tablesPlan.settings.fiveOrMoreCoversDurationErrorMessage');
  }

  const workingDayStartHour = 6;

  for (const shift of this.shifts) {
    if (shift.isActive !== true) {
      continue;
    }

    const fromTotal =
      (shift.fromHour < workingDayStartHour ? shift.fromHour + 24 : shift.fromHour) * 60 +
      (shift.fromMin ?? 0);

    const toTotal =
      (shift.toHour < workingDayStartHour ? shift.toHour + 24 : shift.toHour) * 60 +
      (shift.toMin ?? 0);

    if (fromTotal < toTotal) {
      continue;
    }

    return this.translateService.instant(
      'tablesPlan.settings.shiftFromHourErrorMessage',
      { shift: this.getTitle(shift.type) }
    );
  }

  return null;
}

  setShiftIsActive(isActive: boolean) {

  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}

